import { useCallback, useSyncExternalStore } from "react";
import type { MediaQueryInput } from "@/types/hooks";

type LegacyMediaQueryList = {
  addListener: (listener: () => void) => void;
  removeListener: (listener: () => void) => void;
};

/**
 * useMediaQuery
 *
 * Returns whether the provided media query currently matches.
 * Returns `undefined` only during SSR and the hydration render; on
 * client-side mounts (e.g. SPA navigations) the real value is available in
 * the first render, so variant-switching callers don't paint one variant and
 * then swap — a swap that shifts layout and, via scroll anchoring, drags the
 * browser's restored scroll position along with it.
 *
 * @param queryString - a valid window.matchMedia query string
 */
export const useMediaQuery = (
  queryString: MediaQueryInput,
): boolean | undefined => {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      try {
        const mediaQuery = window.matchMedia(queryString);
        if (typeof mediaQuery.addEventListener === "function") {
          mediaQuery.addEventListener("change", onStoreChange);
          return () => mediaQuery.removeEventListener("change", onStoreChange);
        }
        // Safari <14 exposes only the deprecated listener API, which the DOM
        // lib no longer types on MediaQueryList.
        const legacy = mediaQuery as unknown as LegacyMediaQueryList;
        legacy.addListener(onStoreChange);
        return () => legacy.removeListener(onStoreChange);
      } catch (error) {
        console.error("Failed to subscribe to media query:", error);
        return () => {};
      }
    },
    [queryString],
  );

  const getSnapshot = useCallback((): boolean | undefined => {
    try {
      return window.matchMedia(queryString).matches;
    } catch (error) {
      console.error("Failed to evaluate media query:", error);
      return undefined;
    }
  }, [queryString]);

  const getServerSnapshot = useCallback((): boolean | undefined => {
    return undefined;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};
